(() => {
  let dispose = () => {};

  const initialize = () => {
    dispose();
    const root = document.documentElement;
    if (root.dataset.shPage !== 'index') return;
    const nav = document.querySelector('[data-nb-category-nav]');
    const panel = document.querySelector('[data-nb-products]');
    const status = document.querySelector('[data-nb-category-status]');
    if (!nav || !panel || !status || !window.fetch || !window.DOMParser) return;
    const links = Array.from(nav.querySelectorAll('a[data-nb-category]'));
    const sectionId = panel.dataset.nbSectionId;
    if (!sectionId || !links.some(link => link.dataset.nbCategory === panel.dataset.nbCollection)) return;
    const isDraft = ['unpublished', 'development'].includes(root.dataset.shThemeRole);
    let selected = panel.dataset.nbCollection;
    let sequence = 0;
    let request;

    const collectionUrl = link => {
      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Unexpected collection origin');
      if (isDraft) url.searchParams.set('preview_theme_id', root.dataset.shThemeId);
      url.searchParams.set('sh_brand', 'naturalistic');
      return url;
    };

    const markSelected = () => {
      links.forEach(link => {
        const active = link.dataset.nbCategory === selected;
        link.setAttribute('aria-pressed', String(active));
        link.classList.toggle('is-active', active);
      });
    };

    const prepareViewAll = () => {
      const link = panel.querySelector('.collection__view-all a');
      if (link) link.href = collectionUrl(link).href;
    };

    const announce = label => {
      status.textContent = `Showing ${panel.dataset.nbCount} products in ${label}.`;
    };

    const select = async link => {
      const current = ++sequence;
      request?.abort();
      const label = link.textContent.trim();
      if (link.dataset.nbCategory === selected) {
        panel.setAttribute('aria-busy', 'false');
        announce(label);
        return;
      }
      request = new AbortController();
      const activeRequest = request;
      const timeout = window.setTimeout(() => activeRequest.abort(), 15000);
      panel.setAttribute('aria-busy', 'true');
      status.textContent = `Loading ${label.toLowerCase()} products…`;
      try {
        const url = collectionUrl(link);
        url.searchParams.set('sections', sectionId);
        const response = await window.fetch(url.href, {
          signal: request.signal, credentials: 'same-origin', cache: 'no-store',
        });
        if (!response.ok) throw new Error('Section request failed');
        const data = await response.json();
        if (typeof data[sectionId] !== 'string') throw new Error('Missing section');
        const html = new DOMParser().parseFromString(data[sectionId], 'text/html');
        const replacement = html.querySelector('[data-nb-products]');
        if (!replacement || replacement.dataset.nbSectionId !== sectionId ||
            replacement.dataset.nbThemeId !== panel.dataset.nbThemeId ||
            replacement.dataset.nbCollection !== link.dataset.nbCategory) {
          throw new Error('Unexpected section');
        }
        if (current !== sequence) return;
        // Native Dawn card components upgrade on insertion. Their scripts/styles
        // are already loaded by the initial section; do not execute response scripts.
        replacement.querySelectorAll('script').forEach(script => script.remove());
        replacement.querySelectorAll('.scroll-trigger').forEach(element => {
          element.classList.remove('scroll-trigger', 'animate--slide-in');
        });
        panel.replaceChildren(...Array.from(replacement.childNodes, node => document.importNode(node, true)));
        selected = replacement.dataset.nbCollection;
        panel.dataset.nbCollection = selected;
        panel.dataset.nbCount = replacement.dataset.nbCount;
        markSelected();
        prepareViewAll();
        announce(label);
      } catch (error) {
        if (current !== sequence) return;
        status.textContent = `We couldn’t load ${label.toLowerCase()} here. Try the category again, or `;
        const fallback = document.createElement('a');
        fallback.href = collectionUrl(link).href;
        fallback.textContent = `view all ${label.toLowerCase()}`;
        status.append(fallback);
        // Keep the previous products and selection, with a working escape route.
      } finally {
        window.clearTimeout(timeout);
        if (current === sequence) panel.setAttribute('aria-busy', 'false');
      }
    };

    const clicked = event => {
      if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[data-nb-category]');
      if (!links.includes(link) || link.target === '_blank') return;
      event.preventDefault();
      return select(link);
    };
    const keyed = event => {
      const link = event.target.closest('a[data-nb-category]');
      if (event.key !== ' ' || !links.includes(link) || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      event.preventDefault();
      return select(link);
    };

    nav.setAttribute('role', 'group');
    links.forEach(link => {
      link.setAttribute('role', 'button');
      link.setAttribute('aria-controls', panel.id);
      link.href = collectionUrl(link).href;
    });
    markSelected();
    prepareViewAll();
    nav.addEventListener('click', clicked);
    nav.addEventListener('keydown', keyed);
    dispose = () => {
      ++sequence;
      request?.abort();
      nav.removeEventListener('click', clicked);
      nav.removeEventListener('keydown', keyed);
      nav.removeAttribute('role');
      links.forEach(link => {
        ['role', 'aria-controls', 'aria-pressed'].forEach(name => link.removeAttribute(name));
        link.classList.remove('is-active');
      });
      panel.setAttribute('aria-busy', 'false');
    };
  };

  initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
